import type { PaymentDurationType, PaymentTerms, TermDuration } from '@hero/schema';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export interface PaymentTermsPreviewLines {
  /** "Invoice issued at the beginning of every week." (or empty if unset) */
  invoice: string;
  /** "Customer pays 45 days after invoice issuance." (or empty if unset) */
  pay: string;
}
/**
 * Convert a {@link PaymentTerms} pair (invoice issuance + time to pay)
 * into two human-readable preview sentences.
 *
 * Used by:
 *   - `PaymentTermsFields` (form preview chips below the two selects)
 *   - `AgreementSummaryCard` (read-only chips in the workspace Info tab)
 *
 * Both halves return `''` when the underlying `TermDuration` is unset
 * so the consumer can hide that chip cleanly.
 *
 * Calendar-anchored vs rolling vs immediate vs manual is handled the
 * same way as in `PaymentTermsFields`:
 *   - `manual` — "You issue invoices manually whenever needed" /
 *     "Customer pays manually when triggered."
 *   - `immediate` — "Invoice issued the same day as the order." /
 *     "Customer pays on the same day."
 *   - `week` / `month` (with `value === 1`) — calendar-anchored
 *     ("beginning of every week" / "1st of every month").
 *   - `week` / `month` (with `value > 1`) and `day` — rolling cadence
 *     ("Invoice issued every {n} weeks", "Customer pays {n} days
 *     after invoice issuance").
 *
 * The pay-side "after X" anchor flips to "after you issue the invoice"
 * when invoicing is `manual` — clearer for the operator who has no
 * fixed cadence to reference.
 */
export function describePaymentTerms(
  t: TFn,
  terms: PaymentTerms | undefined,
): PaymentTermsPreviewLines {
  return {
    invoice: describeInvoiceCadence(t, terms?.invoiceIssuance),
    pay: describePayDelay(t, terms?.timeToPay, terms?.invoiceIssuance?.type),
  };
}

export function describeInvoiceCadence(t: TFn, term: TermDuration | undefined): string {
  if (!term) return '';
  const v = term.value || 1;
  switch (term.type) {
    case 'manual':
      return t('form.payment.preview.invoice_manual', {
        defaultValue: 'You issue invoices manually whenever needed.',
      });
    case 'immediate':
      return t('form.payment.preview.invoice_immediate', {
        defaultValue: 'Invoice issued the same day as the order.',
      });
    case 'week':
      return v === 1
        ? t('form.payment.preview.invoice_weekly', {
            defaultValue: 'Invoice issued at the beginning of every week.',
          })
        : t('form.payment.preview.invoice_weekly_n', {
            value: v,
            defaultValue: `Invoice issued every ${v} weeks.`,
          });
    case 'month':
      return v === 1
        ? t('form.payment.preview.invoice_monthly', {
            defaultValue: 'Invoice issued on the 1st of every month.',
          })
        : t('form.payment.preview.invoice_monthly_n', {
            value: v,
            defaultValue: `Invoice issued every ${v} months.`,
          });
    case 'day':
    default:
      return v === 1
        ? t('form.payment.preview.invoice_daily', { defaultValue: 'Invoice issued every day.' })
        : t('form.payment.preview.invoice_days_n', {
            value: v,
            defaultValue: `Invoice issued every ${v} days.`,
          });
  }
}

export function describePayDelay(
  t: TFn,
  term: TermDuration | undefined,
  invoiceType: PaymentDurationType | undefined,
): string {
  if (!term) return '';
  const v = term.value || 1;
  // When invoicing is manual, frame the pay-by-date around the
  // moment the org issues the invoice instead of "after each
  // invoice" — clearer for the operator.
  const afterAnchor =
    invoiceType === 'manual'
      ? t('form.payment.preview.after_manual_invoice', {
          defaultValue: 'after you issue the invoice',
        })
      : t('form.payment.preview.after_each_invoice', {
          defaultValue: 'after invoice issuance',
        });
  switch (term.type) {
    case 'manual':
      return t('form.payment.preview.pay_manual', {
        defaultValue: 'Customer pays manually when triggered.',
      });
    case 'immediate':
      return t('form.payment.preview.pay_immediate', {
        defaultValue: 'Customer pays on the same day.',
      });
    case 'week':
      return v === 1
        ? t('form.payment.preview.pay_weekly', {
            anchor: afterAnchor,
            defaultValue: `Customer pays at the beginning of the next week ${afterAnchor}.`,
          })
        : t('form.payment.preview.pay_weekly_n', {
            value: v,
            anchor: afterAnchor,
            defaultValue: `Customer pays ${v} weeks ${afterAnchor}.`,
          });
    case 'month':
      return v === 1
        ? t('form.payment.preview.pay_monthly', {
            anchor: afterAnchor,
            defaultValue: `Customer pays on the 1st of the next month ${afterAnchor}.`,
          })
        : t('form.payment.preview.pay_monthly_n', {
            value: v,
            anchor: afterAnchor,
            defaultValue: `Customer pays ${v} months ${afterAnchor}.`,
          });
    case 'day':
    default:
      return v === 1
        ? t('form.payment.preview.pay_daily', {
            anchor: afterAnchor,
            defaultValue: `Customer pays in one day ${afterAnchor}.`,
          })
        : t('form.payment.preview.pay_days_n', {
            value: v,
            anchor: afterAnchor,
            defaultValue: `Customer pays ${v} days ${afterAnchor}.`,
          });
  }
}

export interface PaymentTermsShortLines {
  /** Compact value for the invoice side (e.g. "Monthly", "30 days", "Manual"). Empty if unset. */
  invoice: string;
  /** Compact value for the pay side (e.g. "Weekly", "30 days", "Same day"). Empty if unset. */
  pay: string;
}

/**
 * Compact value-only formatting for a {@link PaymentTerms} pair —
 * "Monthly", "30 days", "Manual", etc. — suitable for label/value
 * pairs in dense surfaces (workspace agreement card, list rows).
 *
 * Differs from {@link describePaymentTerms} which produces full
 * sentences ("Customer pays 30 days after invoice issuance."); this
 * helper drops the verb and the anchor and keeps just the cadence
 * label, so callers can render `Invoice: Monthly` / `Pay: 30 days`.
 *
 * Both halves return `''` when the underlying `TermDuration` is
 * unset so the caller can hide that pill cleanly.
 */
export function formatPaymentTermsShort(
  t: TFn,
  terms: PaymentTerms | undefined,
): PaymentTermsShortLines {
  return {
    invoice: formatTermShort(t, terms?.invoiceIssuance),
    pay: formatTermShort(t, terms?.timeToPay),
  };
}

/**
 * Cadence-only label for a single {@link TermDuration}. Calendar-
 * anchored shapes (`week` / `month` with `value === 1`) collapse to
 * "Weekly" / "Monthly" — the calendar anchor only matters in the
 * full-sentence preview, not in the compact pill.
 */
export function formatTermShort(t: TFn, term: TermDuration | undefined): string {
  if (!term) return '';
  const v = term.value || 1;
  switch (term.type) {
    case 'manual':
      return t('form.payment.short.manual', { defaultValue: 'Manual' });
    case 'immediate':
      return t('form.payment.short.same_day', { defaultValue: 'Same day' });
    case 'week':
      return v === 1
        ? t('form.payment.short.weekly', { defaultValue: 'Weekly' })
        : t('form.payment.short.weeks_n', {
            value: v,
            defaultValue: `${v} weeks`,
          });
    case 'month':
      return v === 1
        ? t('form.payment.short.monthly', { defaultValue: 'Monthly' })
        : t('form.payment.short.months_n', {
            value: v,
            defaultValue: `${v} months`,
          });
    case 'day':
    default:
      return v === 1
        ? t('form.payment.short.day', { defaultValue: '1 day' })
        : t('form.payment.short.days_n', {
            value: v,
            defaultValue: `${v} days`,
          });
  }
}
